import { Command } from 'commander';
import { relative } from 'path';
import { ApiClient } from '../client.js';
import { confirm, promptLine } from '../prompt.js';
import { isInteractive } from '../env.js';
import { findGitRoot } from '../config-as-code/workspace.js';
import { readRootConfig, writeRootConfig, ROOT_CONFIG_FILE } from '../config-as-code/root-config.js';
import { findInstalledSkills, SKILL_INSTALL_CMD } from '../skill-check.js';
import type { MeResponse } from './whoami.js';

type Org = NonNullable<MeResponse['orgs']>[number];

export const initCommand = new Command('init')
  .description(`Bind this repository to an organization by writing ${ROOT_CONFIG_FILE} (the CLI then operates against that org)`)
  .option('-y, --yes', 'Skip the overwrite confirmation')
  .action(async function (this: Command, opts: { yes?: boolean }) {
    // Direct mode reuses the global --org (no local option, so it isn't shadowed). Read it
    // explicitly rather than via resolveActiveOrg(), so an existing .rekor.yaml isn't mistaken
    // for an explicit choice while we're re-binding.
    const directOrg = this.optsWithGlobals()['org'] as string | undefined;

    const gitRoot = findGitRoot();
    if (!gitRoot) throw new Error(`Not inside a git repository — ${ROOT_CONFIG_FILE} lives at the repo root.`);

    const client = new ApiClient();
    const me = await client.request<MeResponse>('GET', '/v1/auth/me');
    const orgs = me.orgs ?? [];

    if (orgs.length === 0) {
      if (me.auth_kind !== 'jwt') {
        throw new Error(
          '`rekor init` is a human login workflow — a rec_ token is already bound to its org. ' +
          'Use `--org <id>` or REKOR_ORG to override per command instead.',
        );
      }
      throw new Error('You are not a member of any organization. Create one in the Rekor app first.');
    }

    const selected = directOrg
      ? selectDirect(orgs, directOrg)
      : await selectInteractive(orgs);

    // Confirm before clobbering an existing binding.
    const existing = readRootConfig(gitRoot);
    if (existing && !opts.yes) {
      const label = existing.org_name ? `${existing.org_name} (${existing.org_id})` : existing.org_id;
      console.log(`This repo is currently bound to ${label}.`);
      if (!(await confirm(`Rebind to ${selected.name} (${selected.org_id})?`))) {
        console.log('Aborted.');
        return;
      }
    }

    const path = writeRootConfig(gitRoot, { org_id: selected.org_id, org_name: selected.name });
    console.log(`Bound this repo to ${selected.name} (${selected.org_id}).`);
    console.log(`Wrote ${relative(gitRoot, path)} — commit it to share the binding.`);
    // Activation step: the skill is how an AI agent learns to drive Rekor. The update nudge only
    // fires once a skill is installed, so point first-timers at it here — but don't nag a repo that
    // already has it.
    if (findInstalledSkills(gitRoot).length === 0) {
      console.log(`Install the Rekor skill in your AI agent: \`${SKILL_INSTALL_CMD}\``);
    }
    console.log('Next: `rekor databases list`, or `rekor pull <preview>` to fetch config.');
  });

/** --org mode: the id must be one the caller is actually a member of. */
function selectDirect(orgs: Org[], orgId: string): Org {
  const match = orgs.find((o) => o.org_id === orgId);
  if (!match) {
    const available = orgs.map((o) => `  ${o.name} (${o.org_id})`).join('\n');
    throw new Error(`You are not a member of organization '${orgId}'. Your organizations:\n${available}`);
  }
  return match;
}

/** Auto-select a sole org, else prompt by number (requires a TTY). */
async function selectInteractive(orgs: Org[]): Promise<Org> {
  if (orgs.length === 1) {
    console.log(`Organization: ${orgs[0]!.name} (${orgs[0]!.org_id})`);
    return orgs[0]!;
  }
  if (!isInteractive()) {
    throw new Error('Multiple organizations — pass `--org <id>` (or set REKOR_ORG) to choose one non-interactively.');
  }
  console.log('Organizations:');
  orgs.forEach((o, i) => console.log(`  ${i + 1}. ${o.name} (${o.org_id})`));
  const answer = await promptLine(`Select an organization [1-${orgs.length}]: `);
  const idx = Number.parseInt(answer, 10) - 1;
  if (!Number.isInteger(idx) || idx < 0 || idx >= orgs.length) {
    throw new Error('Invalid selection.');
  }
  return orgs[idx]!;
}
